import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_PRODUCTION_REQUEST_LINES,
  COL_SCHEDULE_SLOTS,
} from '../../config/collections'
import { matchesListSearch } from '../../lib/listSearch'
import { requestLineStatusLabel } from '../../lib/requestLineStatus'
import { formatDateRu, formatQty } from '../../lib/scheduleCalculations'
import { pb } from '../../pocketbase'
import { Alert, PageHeader, PageShell, SearchField } from '../../components/ui'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import {
  btn,
  cn,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'
import type { ProductionRequestLineRecord, ScheduleSlotRecord } from '../../types/records'

type FulfillmentRow = {
  line: ProductionRequestLineRecord
  ordered: number
  produced: number
  shipped: number
  toProduce: number
  toShip: number
}

function sumProducedForLine(slots: ScheduleSlotRecord[], lineId: string): number {
  return slots
    .filter((s) => {
      const rl = typeof s.requestLine === 'string' ? s.requestLine : ''
      return rl === lineId && s.status === 'completed'
    })
    .reduce((acc, s) => acc + (Number(s.actualQuantity) || Number(s.plannedQuantity) || 0), 0)
}

export function FulfillmentPage() {
  const [lines, setLines] = useState<ProductionRequestLineRecord[]>([])
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [hideCompleted, setHideCompleted] = useState(true)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const [lineList, slotList] = await Promise.all([
        pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
          filter: 'status != "cancelled"',
          expand: 'product,request',
          sort: '-priority,plannedShipManager',
        }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: 'status = "completed" || status = "in_progress"',
        }),
      ])
      setLines(lineList)
      setSlots(slotList)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setLines([])
      setSlots([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo((): FulfillmentRow[] => {
    return lines.map((line) => {
      const ordered = Number(line.quantity) || 0
      const shipped = Number(line.shippedQty ?? 0) || 0
      const produced = sumProducedForLine(slots, line.id)
      return {
        line,
        ordered,
        produced,
        shipped,
        toProduce: Math.max(0, ordered - produced),
        toShip: Math.max(0, ordered - shipped),
      }
    })
  }, [lines, slots])

  const displayedRows = useMemo(() => {
    let list = rows
    if (hideCompleted) {
      list = list.filter((r) => r.toShip > 0 || r.toProduce > 0 || r.line.status === 'in_progress')
    }
    if (!searchQuery.trim()) return list
    return list.filter(({ line }) =>
      matchesListSearch(
        searchQuery,
        line.expand?.product?.name,
        line.expand?.product?.code,
        line.expand?.request?.clientName,
        requestLineStatusLabel(line.status),
      ),
    )
  }, [rows, hideCompleted, searchQuery])

  const totals = useMemo(() => {
    return displayedRows.reduce(
      (acc, r) => ({
        ordered: acc.ordered + r.ordered,
        produced: acc.produced + r.produced,
        shipped: acc.shipped + r.shipped,
      }),
      { ordered: 0, produced: 0, shipped: 0 },
    )
  }, [displayedRows])

  const fulfillmentCsvColumns: CsvColumn<FulfillmentRow>[] = useMemo(
    () => [
      { header: 'Клиент', value: (r) => r.line.expand?.request?.clientName ?? '' },
      { header: 'Продукция', value: (r) => r.line.expand?.product?.name ?? '' },
      { header: 'Код', value: (r) => r.line.expand?.product?.code ?? '' },
      { header: 'Статус', value: (r) => requestLineStatusLabel(r.line.status) },
      { header: 'Заказано', value: (r) => r.ordered },
      { header: 'Произведено', value: (r) => r.produced },
      { header: 'Отгружено', value: (r) => r.shipped },
      { header: 'К производству', value: (r) => r.toProduce },
      { header: 'К отгрузке', value: (r) => r.toShip },
      {
        header: 'Срок',
        value: (r) => (r.line.dueDate ? formatDateRu(r.line.dueDate) : ''),
      },
    ],
    [],
  )

  return (
    <PageShell>
      <PageHeader
        title="Исполнение заказов"
        lead="Сводка по строкам заявок: заказано, произведено (по завершённым слотам графика) и отгружено."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        {displayedRows.length > 0 && (
          <ExportCsvButton filename="fulfillment" rows={displayedRows} columns={fulfillmentCsvColumns} />
        )}
        <label className="flex items-center gap-2 text-sm text-su-text">
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.target.checked)}
          />
          Скрыть полностью закрытые
        </label>
        <div className="flex min-w-40 flex-1 justify-end">
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Клиент, продукция…"
            aria-label="Поиск исполнения"
          />
        </div>
      </div>

      {!loading && displayedRows.length > 0 && (
        <p className="mb-3 text-sm text-su-text">
          Итого по таблице: заказано <strong>{formatQty(totals.ordered)}</strong>, произведено{' '}
          <strong>{formatQty(totals.produced)}</strong>, отгружено <strong>{formatQty(totals.shipped)}</strong>
        </p>
      )}

      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Клиент</th>
              <th className={tableHeadClass}>Продукция</th>
              <th className={tableHeadClass}>Статус</th>
              <th className={tableHeadClass}>Заказано</th>
              <th className={tableHeadClass}>Произведено</th>
              <th className={tableHeadClass}>Отгружено</th>
              <th className={tableHeadClass}>Ост. произв.</th>
              <th className={tableHeadClass}>Ост. отгруз.</th>
              <th className={tableHeadClass}>План отгрузки</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={9}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && displayedRows.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={9}>
                  <em>{rows.length === 0 ? 'Нет строк заявок' : 'Ничего не найдено'}</em>
                </td>
              </tr>
            )}
            {!loading &&
              displayedRows.map(({ line, ordered, produced, shipped, toProduce, toShip }) => {
                const product = line.expand?.product
                const behindProduction = toProduce > 0 && produced < ordered * 0.5
                const behindShipment = toShip > 0 && shipped < ordered * 0.5
                return (
                  <tr
                    key={line.id}
                    className={cn(
                      tableRowHoverClass,
                      (behindProduction || behindShipment) && 'bg-amber-50/50 dark:bg-amber-950/15',
                    )}
                  >
                    <td className={tableCellClass}>{line.expand?.request?.clientName ?? '—'}</td>
                    <td className={tableCellClass}>
                      {product?.name ?? '—'}
                      {product?.code?.trim() ? (
                        <span className="ml-1 text-xs text-su-text">({product.code})</span>
                      ) : null}
                    </td>
                    <td className={tableCellClass}>{requestLineStatusLabel(line.status)}</td>
                    <td className={tableCellClass}>{formatQty(ordered)}</td>
                    <td className={cn(tableCellClass, toProduce > 0 && 'text-amber-800 dark:text-amber-200')}>
                      {formatQty(produced)}
                    </td>
                    <td className={cn(tableCellClass, toShip > 0 && 'text-amber-800 dark:text-amber-200')}>
                      {formatQty(shipped)}
                    </td>
                    <td className={tableCellClass}>{toProduce > 0 ? formatQty(toProduce) : '—'}</td>
                    <td className={tableCellClass}>{toShip > 0 ? formatQty(toShip) : '—'}</td>
                    <td className={tableCellClass}>{formatDateRu(line.plannedShipManager)}</td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>
    </PageShell>
  )
}
